"use client";

import { getSession } from "next-auth/react";
import { invalidateApiSessionCache } from "@/app/api";
import type { Session } from "next-auth";
import { useRef, useEffect, useState } from "react";

import { getInitials, isUserAdmin, cn } from "@/lib/utils";
import { useWindowResizeSizing, useOutOfBoundsHitToggle } from "@/lib/hooks";
import {
	MAX_SAVED_LOGIN_ACCOUNTS,
	canUseAccountSwitcher,
	getSavedAccountDisplayName,
	getSavedLoginAccounts,
	isSavedLoginAccountSignedOut,
	markSavedLoginAccountSignedOut,
	moveSavedAccountToTop,
	removeSavedLoginAccount,
	saveCurrentAccountIfEligible,
} from "@/lib/auth/saved-accounts";
import type { SavedLoginAccount } from "@/lib/auth/saved-accounts";
import AvatarWrapper from "./AvatarWrapper";
import AccountSwitcherAccountRow from "./AccountSwitcherAccountRow";

import NavDropdownItem from "@/app/components/Navigation/components/NavDropdownItem";
import LogoutModal from "@/app/profile/components/LogoutModal";
import AuthDialog from "@/components/auth/AuthDialog";
import AccountSwitcherTwoFactorDialog from "@/components/auth/AccountSwitcherTwoFactorDialog";
import { useToast } from "@/components/ui/use-toast";
import {
	executeAccountSwitch,
	parkSessionForAccountSwitcher,
	type AccountSwitchResult,
} from "@/lib/auth/switch-account";

import ChatIcon from "@/components/icons/ChatIcon";
import OrderIcon from "@/components/icons/OrderIcon";
import SavedIcon from "@/components/icons/SavedIcon";
import PreferencesIcon from "@/components/icons/PreferencesIcon";
import SettingsIcon from "@/components/icons/SettingsIcon";
import AffiliatesIcon from "@/components/icons/AffiliatesIcon";
import DirectoryIcon from "@/components/icons/DirectoryIcon";
import LogoutIcon from "@/components/icons/LogoutIcon";

const NAV_DROPDOWN_OPEN_EVENT = "techxeed:nav-dropdown-open";
const NAV_DROPDOWN_ID = "profile";
const ACCOUNT_SWITCHER_DROPDOWN_ID = "account-switcher";
const ACCOUNT_SWITCHER_LONG_PRESS_MS = 520;

function getCurrentReturnUrl() {
	if (typeof window === "undefined") return "/";
	return `${window.location.pathname}${window.location.search}${window.location.hash}` || "/";
}

function reloadCurrentPage() {
	if (typeof window === "undefined") return;
	window.history.replaceState(null, "", getCurrentReturnUrl());
	window.location.reload();
}

type Props = { session: Session | null };
export default function NavProfileButton({ session }: Props) {
	const dropdownMaxHeight = useWindowResizeSizing(0, -100);
	const [showDropdown, setDropdownVisibility] = useState(false);
	const [showAccountSwitcher, setAccountSwitcherVisibility] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLDivElement>(null);
	const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const longPressTriggeredRef = useRef(false);
	const suppressNextClickRef = useRef(false);
	const [isMobile, setIsMobile] = useState(false);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [loginReturnUrl, setLoginReturnUrl] = useState("/");
	const [allowAuthenticatedLogin, setAllowAuthenticatedLogin] = useState(false);
	const [savedAccounts, setSavedAccounts] = useState<SavedLoginAccount[]>([]);
	const [switchingAccountId, setSwitchingAccountId] = useState<string | null>(null);
	const [pending2FAAccount, setPending2FAAccount] = useState<SavedLoginAccount | null>(null);
	const [isSubmitting2FA, setIsSubmitting2FA] = useState(false);
	const { toast } = useToast();

	// Use the same click-outside hook as other nav buttons
	// Type assertion needed because useRef<HTMLDivElement>(null) creates RefObject<HTMLDivElement | null>
	// but the hook expects RefObject<Element> - safe because hook handles null with optional chaining
	useOutOfBoundsHitToggle(buttonRef as React.RefObject<HTMLDivElement>, [showDropdown, setDropdownVisibility]);
	useOutOfBoundsHitToggle(buttonRef as React.RefObject<HTMLDivElement>, [showAccountSwitcher, setAccountSwitcherVisibility]);

	// Detect mobile screen size
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768); // md breakpoint
		};
		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	useEffect(() => {
		const closeForOtherDropdown = (event: Event) => {
			const detail = (event as CustomEvent<string>).detail;
			if (detail !== NAV_DROPDOWN_ID && detail !== ACCOUNT_SWITCHER_DROPDOWN_ID) {
				setDropdownVisibility(false);
				setAccountSwitcherVisibility(false);
			}
		};

		window.addEventListener(NAV_DROPDOWN_OPEN_EVENT, closeForOtherDropdown);
		return () => window.removeEventListener(NAV_DROPDOWN_OPEN_EVENT, closeForOtherDropdown);
	}, []);

	useEffect(() => {
		saveCurrentAccountIfEligible(session);
		setSavedAccounts(getSavedLoginAccounts());
	}, [session?.idToken, session?.user?.id]);

	useEffect(() => {
		return () => {
			if (longPressTimerRef.current) {
				clearTimeout(longPressTimerRef.current);
			}
		};
	}, []);

	const openLogoutModal = () => {
		document.body.style.overflow = "auto";
		document.body.style.paddingRight = "0";
		setDropdownVisibility(false);
		setShowLogoutModal(true);
	};

	const openLoginModal = () => {
		setAllowAuthenticatedLogin(false);
		setLoginReturnUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`);
		setShowLoginModal(true);
	};

	const user = session?.user;
	const isAdmin = user ? isUserAdmin(user.role) : false;
	const canShowAccountSwitcher = user ? canUseAccountSwitcher(session, savedAccounts) : false;
	// SECURITY: Only show Admin Panel button if user is admin/director AND has 2FA enabled
	// Check is2FAEnabled from session (set during login)
	const is2FAEnabled = user ? (user as any).is2FAEnabled : false;
	const showAdminPanel = isAdmin && is2FAEnabled === true;

	const refreshSavedAccounts = () => {
		const accounts = getSavedLoginAccounts();
		setSavedAccounts(accounts);
		return accounts;
	};

	const openAccountSwitcher = () => {
		if (!canShowAccountSwitcher) return;

		saveCurrentAccountIfEligible(session);
		const accounts = refreshSavedAccounts();
		if (accounts.length === 0) {
			toast({
				title: "No saved accounts yet",
				description: "Save an admin or director account first.",
			});
			return;
		}

		setDropdownVisibility(false);
		setAccountSwitcherVisibility(true);
		window.dispatchEvent(new CustomEvent(NAV_DROPDOWN_OPEN_EVENT, { detail: ACCOUNT_SWITCHER_DROPDOWN_ID }));
	};

	const clearLongPressTimer = () => {
		if (longPressTimerRef.current) {
			clearTimeout(longPressTimerRef.current);
			longPressTimerRef.current = null;
		}
	};

	const handleProfilePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
		if (!canShowAccountSwitcher || event.button !== 0) return;

		clearLongPressTimer();
		longPressTriggeredRef.current = false;
		longPressTimerRef.current = setTimeout(() => {
			longPressTriggeredRef.current = true;
			suppressNextClickRef.current = true;
			openAccountSwitcher();
		}, ACCOUNT_SWITCHER_LONG_PRESS_MS);
	};

	const handleProfilePointerEnd = () => {
		clearLongPressTimer();
	};

	const handleProfileClick = () => {
		if (suppressNextClickRef.current || longPressTriggeredRef.current) {
			suppressNextClickRef.current = false;
			longPressTriggeredRef.current = false;
			return;
		}

		setAccountSwitcherVisibility(false);
		const nextOpen = !showDropdown;
		setDropdownVisibility(nextOpen);
		if (nextOpen) {
			window.dispatchEvent(new CustomEvent(NAV_DROPDOWN_OPEN_EVENT, { detail: NAV_DROPDOWN_ID }));
		}
	};

	const removeAccountFromSwitcher = (account: SavedLoginAccount) => {
		removeSavedLoginAccount(account.userId);
		refreshSavedAccounts();
		toast({
			title: "Account removed",
			description: `${getSavedAccountDisplayName(account)} was removed from this device.`,
		});
	};

	const finishSuccessfulSwitch = (account: SavedLoginAccount) => {
		moveSavedAccountToTop(account.userId);
		invalidateApiSessionCache();
		setSwitchingAccountId(null);
		reloadCurrentPage();
	};

	const handleSwitchResult = (result: AccountSwitchResult, account: SavedLoginAccount) => {
		if (result.status === "requires2FA") {
			setPending2FAAccount(account);
			setSwitchingAccountId(null);
			return;
		}

		if (result.status === "unauthorized") {
			markSavedLoginAccountSignedOut(account.userId);
			refreshSavedAccounts();
			setAllowAuthenticatedLogin(true);
			setLoginReturnUrl(getCurrentReturnUrl());
			setShowLoginModal(true);
			setSwitchingAccountId(null);
			toast({
				title: "Login required",
				description: `${getSavedAccountDisplayName(account)}'s saved session expired. Please log in again.`,
			});
			return;
		}

		if (result.status === "error") {
			throw new Error(result.message);
		}

		finishSuccessfulSwitch(account);
	};

	const switchToSavedAccount = async (account: SavedLoginAccount, isCurrent: boolean) => {
		setAccountSwitcherVisibility(false);
		if (isCurrent) return;

		if (isSavedLoginAccountSignedOut(account)) {
			saveCurrentAccountIfEligible(session);
			void parkSessionForAccountSwitcher(session);
			refreshSavedAccounts();
			setAllowAuthenticatedLogin(true);
			setLoginReturnUrl(getCurrentReturnUrl());
			setShowLoginModal(true);
			toast({
				title: "Login required",
				description: `Please log in again as ${getSavedAccountDisplayName(account)}.`,
			});
			return;
		}

		setSwitchingAccountId(account.userId);
		try {
			const result = await executeAccountSwitch(account, session);
			handleSwitchResult(result, account);
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not switch account",
				description: error instanceof Error ? error.message : "Please log in again.",
			});
			setSwitchingAccountId(null);
		}
	};

	const handleAccountSwitch2FA = async (twoFactorToken: string) => {
		if (!pending2FAAccount) return;

		setIsSubmitting2FA(true);
		try {
			const result = await executeAccountSwitch(pending2FAAccount, session, { twoFactorToken });
			if (result.status === "success") {
				const account = pending2FAAccount;
				setPending2FAAccount(null);
				finishSuccessfulSwitch(account);
				return;
			}
			if (result.status === "unauthorized") {
				throw new Error("Session expired. Please log in again.");
			}
			if (result.status === "error") {
				throw new Error(result.message);
			}
			throw new Error("Invalid authentication code. Please try again.");
		} finally {
			setIsSubmitting2FA(false);
		}
	};

	const addAccountFromSwitcher = async () => {
		const liveSession = await getSession();
		const activeSession = liveSession ?? session;
		await parkSessionForAccountSwitcher(activeSession);
		const savedCurrent = saveCurrentAccountIfEligible(activeSession);
		const accounts = refreshSavedAccounts();
		if (!savedCurrent && accounts.length === 0) {
			toast({
				variant: "destructive",
				title: "Could not save current account",
				description: "Please log out and log in again before adding another account.",
			});
			return;
		}
		if (accounts.length >= MAX_SAVED_LOGIN_ACCOUNTS) {
			toast({
				title: "Maximum 10 accounts reached",
				description: "Remove an account before adding another one.",
			});
			return;
		}

		setDropdownVisibility(false);
		setAccountSwitcherVisibility(false);
		setAllowAuthenticatedLogin(true);
		setLoginReturnUrl("/");
		setShowLoginModal(true);
	};

	return (
		<>
			{!user ? (
				<button
					type="button"
					className="ml-1 flex w-12 items-center justify-center text-sm2 font-semibold"
					onClick={openLoginModal}
				>
					Login
				</button>
			) : (
				<div
					ref={buttonRef}
					className="group relative ml-1"
				>
					<button
						aria-haspopup="menu"
						aria-controls="profile-dropdown"
						aria-expanded={showDropdown || showAccountSwitcher}
						aria-label="User profile menu"
						className="relative z-[70] flex size-12 min-h-[44px] min-w-[44px] items-center justify-center"
						onPointerDown={handleProfilePointerDown}
						onPointerUp={handleProfilePointerEnd}
						onPointerCancel={handleProfilePointerEnd}
						onPointerLeave={handleProfilePointerEnd}
						onContextMenu={(event) => {
							if (canShowAccountSwitcher) event.preventDefault();
						}}
						onClick={handleProfileClick}
					>
						<AvatarWrapper
							src={user.avatar}
							alt="avatar"
							fallback={getInitials(user.firstName, user.lastName)}
							firstName={user.firstName}
							lastName={user.lastName}
							className={cn(
								"size-full rounded-full border-2 border-tertiary text-base font-semibold dark:text-foreground",
							)}
						/>
					</button>
					<div
						ref={dropdownRef}
						id="profile-dropdown"
						className="fixed left-1/2 top-[calc(90px+1.5rem)] z-[60] md:absolute md:left-auto md:right-0 md:top-5"
						style={{
							display: showDropdown ? 'block' : 'none',
							// Keep pointer events active as long as dropdown is shown, not just when hovered
							// This allows clicking links even if hover state is temporarily lost
							pointerEvents: showDropdown ? 'auto' : 'none',
							transform: isMobile ? 'translateX(-50%)' : 'none',
							width: isMobile ? 'calc(100vw - 2rem)' : 'auto',
							maxWidth: isMobile ? 'calc(100vw - 2rem)' : undefined,
						}}
					>
						<div aria-hidden className="h-12" />
						<ul
							className="dropdown-glassmorphism grid w-full overflow-y-auto overflow-x-hidden rounded-2xl md:min-w-72"
							style={{
								maxHeight: `${dropdownMaxHeight.current}px`,
								border: '1px solid var(--glass-border)',
								boxShadow: 'var(--glass-shadow)',
								background: 'var(--glass-background)',
								minWidth: isMobile ? undefined : '18rem', // 72 * 0.25rem = 18rem
							}}
						>
							<li className="flex min-h-8 flex-col items-center p-4 pb-2 text-[19px] leading-snug ">
								Hi, {user.firstName}
								{/* Hi, {user.firstName} {user.lastName} */}
								{/* <p className="text-sm text-grey-foreground">{user.email}</p> */}
							</li>
							<NavDropdownItem
								title="Support"
								href="/profile/inbox"
								icon={<ChatIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
								iconClassName="-ml-1"
							/>
							<NavDropdownItem
								title="Orders"
								href="/profile/order-history"
								icon={<OrderIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
							/>
							<NavDropdownItem
								title="Saved"
								href="/profile/saved"
								icon={<SavedIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
							/>
							<NavDropdownItem
								title="Preferences"
								href="/profile/preferences"
								icon={<PreferencesIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
							/>
							<NavDropdownItem
								title="Settings"
								href="/profile/settings"
								icon={<SettingsIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
							/>
							<NavDropdownItem
								// title={isAdmin ? "Admin Affiliates" : "Affiliates"}
								// href={isAdmin ? "/admin/affiliates" : "/profile/affiliates"}
								title="Affiliates"
								href="/profile/affiliates"
								icon={<AffiliatesIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
							/>
							<NavDropdownItem
								title="Automation"
								href="/profile/automation"
								icon={<DirectoryIcon className="size-6 shrink-0 text-foreground" style={{ color: "inherit" }} />}
							/>
							{showAdminPanel && (
								<NavDropdownItem
									title="Admin Panel"
									href="/admin/dashboard"
									icon={<SettingsIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
								/>
							)}
							<NavDropdownItem
								title="Logout"
								onClick={openLogoutModal}
								icon={<LogoutIcon className="size-6 shrink-0 text-foreground" style={{ color: 'inherit' }} />}
							/>
						</ul>
					</div>
					<div
						id="account-switcher-dropdown"
						className="fixed left-1/2 top-[calc(90px+1.5rem)] z-[60] md:absolute md:left-auto md:right-0 md:top-5"
						style={{
							display: showAccountSwitcher ? 'block' : 'none',
							pointerEvents: showAccountSwitcher ? 'auto' : 'none',
							transform: isMobile ? 'translateX(-50%)' : 'none',
							width: isMobile ? 'calc(100vw - 2rem)' : 'auto',
							maxWidth: isMobile ? 'calc(100vw - 2rem)' : undefined,
						}}
					>
						<div aria-hidden className="h-12" />
						<div
							className="dropdown-glassmorphism flex w-full flex-col overflow-hidden rounded-2xl md:min-w-80"
							style={{
								maxHeight: `${dropdownMaxHeight.current}px`,
								border: '1px solid var(--glass-border)',
								boxShadow: 'var(--glass-shadow)',
								background: 'var(--glass-background)',
								minWidth: isMobile ? undefined : '20rem',
							}}
						>
							<div className="flex items-center justify-between px-5 pb-2 pt-4">
								<p className="text-sm2 font-semibold text-foreground">Switch account</p>
								<span className="text-xxsm text-muted-foreground">
									{savedAccounts.length}/{MAX_SAVED_LOGIN_ACCOUNTS}
								</span>
							</div>
							<div className="muted-scrollbar min-h-0 overflow-y-auto">
								{savedAccounts.map((account) => {
									const isCurrent = String(account.userId) === String(user.id);
									const isSwitching = switchingAccountId === account.userId;

									return (
										<AccountSwitcherAccountRow
											key={account.userId}
											account={account}
											isCurrent={isCurrent}
											isSwitching={isSwitching}
											disabled={switchingAccountId !== null}
											onSelect={() => switchToSavedAccount(account, isCurrent)}
											onRemove={() => removeAccountFromSwitcher(account)}
										/>
									);
								})}
							</div>
							<button
								type="button"
								className="flex w-full items-center gap-3 border-t border-tertiary/25 px-5 py-4 text-left transition-colors hover:bg-accent/10"
								onClick={addAccountFromSwitcher}
							>
								<span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-tertiary/10 text-lg font-semibold text-tertiary">
									+
								</span>
								<span className="text-sm font-semibold text-foreground">
									{savedAccounts.length >= MAX_SAVED_LOGIN_ACCOUNTS ? "Maximum 10 accounts reached" : "Add account"}
								</span>
							</button>
						</div>
					</div>
				</div>
			)}
			<LogoutModal open={showLogoutModal} onOpenChange={setShowLogoutModal} />
			<AuthDialog
				open={showLoginModal}
				onOpenChange={(open) => {
					setShowLoginModal(open);
					if (!open) setAllowAuthenticatedLogin(false);
				}}
				returnUrl={loginReturnUrl}
				allowAuthenticatedLogin={allowAuthenticatedLogin}
				onAuthenticated={async () => {
					setShowLoginModal(false);
					if (allowAuthenticatedLogin) {
						const liveSession = await getSession();
						saveCurrentAccountIfEligible(liveSession);
						invalidateApiSessionCache();
						reloadCurrentPage();
					}
				}}
			/>
			<AccountSwitcherTwoFactorDialog
				open={!!pending2FAAccount}
				account={pending2FAAccount}
				isSubmitting={isSubmitting2FA}
				onOpenChange={(open) => {
					if (!open) setPending2FAAccount(null);
				}}
				onSubmit={handleAccountSwitch2FA}
			/>
		</>
	);
}
